/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: components\CreateClientModal.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the openvpn module.
----------------------------------------------------------------*/


import React, { useState } from 'react';
import { X, Shield } from 'lucide-react';
import { useI18n } from '../../../hooks/useI18n';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import type { CreateClientRequest, ClientFormat } from '../types';

interface CreateClientModalProps {
    isOpen: boolean;
    onClose: () => void;
    onCreate: (data: CreateClientRequest) => Promise<void>;
    isCreating: boolean;
}

export function CreateClientModal({ isOpen, onClose, onCreate, isCreating }: CreateClientModalProps) {
    const { t } = useI18n();
    const [name, setName] = useState('');
    const [fixedIp, setFixedIp] = useState('');
    const [format, setFormat] = useState<ClientFormat>('generic');
    const [error, setError] = useState('');

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        if (!name.trim()) {
            setError(t('common.validation.required', 'This field is required'));
            return;
        }

        // Validate name format (alphanumeric, hyphens, underscores)
        if (!/^[a-zA-Z0-9_-]+$/.test(name)) {
            setError(t('openvpn.modal.nameFormatError', 'Name can only contain letters, numbers, hyphens and underscores'));
            return;
        }

        // Validate IP format if provided
        if (fixedIp.trim() && !/^(\d{1,3}\.){3}\d{1,3}$/.test(fixedIp.trim())) {
            setError(t('openvpn.modal.ipFormatError', 'Invalid IP address format'));
            return;
        }

        await onCreate({
            name: name.trim(),
            fixedIp: fixedIp.trim() || undefined,
            format
        });

        // Reset form
        setName('');
        setFixedIp('');
        setFormat('generic');
    };

    const handleClose = () => {
        setName('');
        setFixedIp('');
        setFormat('generic');
        setError('');
        onClose();
    };

    if (!isOpen) return null;

    const selectClasses = 'w-full px-3 py-2 text-sm border rounded bg-background text-foreground border-border focus:outline-none focus:ring-1 focus:ring-primary';

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-black/50 backdrop-blur-sm"
                onClick={handleClose}
            />

            {/* Modal */}
            <div className="relative bg-card border border-border rounded-lg shadow-xl w-full max-w-md mx-4">
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-border">
                    <div className="flex items-center gap-2">
                        <Shield className="h-5 w-5 text-primary" />
                        <h2 className="text-lg font-semibold text-foreground">
                            {t('openvpn.modal.createTitle', 'Create VPN Client')}
                        </h2>
                    </div>
                    <button
                        onClick={handleClose}
                        className="p-1 rounded hover:bg-muted transition-colors"
                    >
                        <X className="h-5 w-5 text-muted-foreground" />
                    </button>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="p-4 space-y-4">
                    {/* Client Name */}
                    <div className="space-y-2">
                        <Label htmlFor="clientName" required>
                            {t('openvpn.modal.clientName', 'Client Name')}
                        </Label>
                        <Input
                            id="clientName"
                            value={name}
                            onChange={(e) => {
                                setName(e.target.value);
                                setError('');
                            }}
                            placeholder={t('openvpn.modal.clientNamePlaceholder', 'Enter client name')}
                            className={error ? 'border-red-500' : ''}
                        />
                        {error && (
                            <p className="text-xs text-red-500">{error}</p>
                        )}
                    </div>

                    {/* Fixed IP */}
                    <div className="space-y-2">
                        <Label htmlFor="fixedIp">
                            {t('openvpn.modal.fixedIp', 'Fixed IP Address')}
                        </Label>
                        <Input
                            id="fixedIp"
                            value={fixedIp}
                            onChange={(e) => setFixedIp(e.target.value)}
                            placeholder={t('openvpn.modal.fixedIpPlaceholder', 'e.g., 10.8.0.50 (optional)')}
                        />
                        <p className="text-xs text-muted-foreground">
                            {t('openvpn.modal.fixedIpHelp', 'Leave empty for automatic IP assignment')}
                        </p>
                    </div>

                    {/* Format */}
                    <div className="space-y-2">
                        <Label htmlFor="clientFormat">
                            {t('openvpn.modal.format', 'Format')}
                        </Label>
                        <select
                            id="clientFormat"
                            value={format}
                            onChange={(e) => setFormat(e.target.value as ClientFormat)}
                            className={selectClasses}
                        >
                            <option value="generic">{t('openvpn.clients.formats.generic', 'Generic')}</option>
                            <option value="grandstream">{t('openvpn.clients.formats.grandstream', 'Grandstream')}</option>
                            <option value="yealink">{t('openvpn.clients.formats.yealink', 'Yealink')}</option>
                            <option value="fanvil">{t('openvpn.clients.formats.fanvil', 'Fanvil')}</option>
                        </select>
                        <p className="text-xs text-muted-foreground">
                            {t('openvpn.modal.formatHelp', 'Select the format based on the device type')}
                        </p>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center justify-end gap-2 pt-4">
                        <button
                            type="button"
                            onClick={handleClose}
                            disabled={isCreating}
                            className="px-4 py-2 text-sm border border-border rounded-lg hover:bg-muted transition-colors disabled:opacity-50"
                        >
                            {t('common.cancel', 'Cancel')}
                        </button>
                        <button
                            type="submit"
                            disabled={isCreating}
                            className="flex items-center gap-2 px-4 py-2 text-sm bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50"
                        >
                            {isCreating ? (
                                <>
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current" />
                                    {t('common.creating', 'Creating...')}
                                </>
                            ) : (
                                t('common.create', 'Create')
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
